// Content module hooks
import { useState } from 'react';
import { contentService } from '../services/contentService';
import { Content, ContentForm } from '../types';
import { useApi, useMutation } from '../../../shared/hooks/useApi';

export interface UseContentsProps {
  waypointName?: string;
  version?: number;
  museumCode?: string;
}

export function useContents({ waypointName, version, museumCode }: UseContentsProps = {}) {
  return useApi(
    () => contentService.getContents({ waypoint_name: waypointName, version, museum_code: museumCode }),
    [waypointName, version, museumCode],
    true, // Always fetch - museumCode can be undefined for "All Museums"
  );
}

export function useContent(waypointName: string, version: number = 1) {
  return useApi(() => contentService.getContent(waypointName, version), [waypointName, version], !!waypointName);
}

export function useCreateContent() {
  return useMutation(contentService.createContent, {
    onSuccess: () => {
      console.log('Content template created successfully');
    },
    onError: error => {
      console.error('Failed to create content template:', error);
    },
  });
}

export function useUpdateContent() {
  return useMutation(
    ({ waypointName, version, data }: { waypointName: string; version: number; data: Partial<ContentForm> }) =>
      contentService.updateContent(waypointName, version, data),
    {
      onSuccess: () => {
        console.log('Content template updated successfully');
      },
      onError: error => {
        console.error('Failed to update content template:', error);
      },
    },
  );
}

export function useDeleteContent(onErrorSnackbar?: (msg: string) => void) {
  return useMutation(
    ({ waypointName, version }: { waypointName: string; version: number }) =>
      contentService.deleteContent(waypointName, version),
    {
      onSuccess: () => {
        console.log('Content template deleted successfully');
      },
      onError: (error: any) => {
        let msg =
          error?.response?.data?.detail && typeof error?.response?.data?.detail === 'string'
            ? error.response.data.detail
            : error?.response?.data?.error && typeof error?.response?.data?.error === 'string'
              ? error.response.data.error
              : error?.message && error?.message !== 'Request failed with status code 400'
                ? error.message
                : 'Operation failed. The item may be referenced and cannot be deleted.';
        if (msg === 'Operation failed. The item may be referenced and cannot be deleted.') {
          console.warn('[HOOK:content] using fallback for snackbar:', error);
        }
        console.error('[HOOK:content] error message for snackbar:', msg, error);
        if (onErrorSnackbar) onErrorSnackbar(msg);
      },
    },
  );
}

export function useContentFiles(fileType: string = 'image') {
  return useApi(() => contentService.getFiles(fileType), [fileType]);
}

export function useUploadContentFile() {
  return useMutation(
    ({ file, fileType }: { file: File; fileType?: string }) => contentService.uploadFile(file, fileType),
    {
      onSuccess: () => {
        console.log('File uploaded successfully');
      },
      onError: error => {
        console.error('Failed to upload file:', error);
      },
    },
  );
}

export function useDeleteContentFile(
  refetchContents?: () => void,
  refetchTours?: () => void,
  onErrorSnackbar?: (msg: string) => void,
) {
  return useMutation(contentService.deleteFile, {
    onSuccess: _data => {
      if (refetchContents) refetchContents();
      if (refetchTours) refetchTours();
    },
    onError: (error: any) => {
      let msg =
        error?.response?.data?.detail && typeof error?.response?.data?.detail === 'string'
          ? error.response.data.detail
          : error?.response?.data?.error && typeof error?.response?.data?.error === 'string'
            ? error.response.data.error
            : error?.message && error?.message !== 'Request failed with status code 400'
              ? error.message
              : 'Operation failed. The item may be referenced and cannot be deleted.';
      if (msg === 'Operation failed. The item may be referenced and cannot be deleted.') {
        console.warn('[HOOK:file] using fallback for snackbar:', error);
      }
      console.error('[HOOK:file] error message for snackbar:', msg, error);
      if (onErrorSnackbar) onErrorSnackbar(msg);
    },
  });
}

// Utility hooks
export function useContentForWaypoint() {
  const [checking, setChecking] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [content, setContent] = useState<Content | null>(null);

  const checkContentForWaypoint = async (waypointName: string, version: number = 1) => {
    setChecking(true);
    try {
      const foundContent = await contentService.getContentForWaypoint(waypointName, version);
      setContent(foundContent);
      setHasContent(foundContent !== null);
    } catch (error) {
      console.error('Error checking content for waypoint:', error);
      setHasContent(false);
      setContent(null);
    } finally {
      setChecking(false);
    }
  };

  return {
    checking,
    hasContent,
    content,
    checkContentForWaypoint,
  };
}

export function useContentVersions(waypointName: string) {
  return useApi(() => contentService.getContentVersions(waypointName), [waypointName], !!waypointName);
}

export function useContentUsage(waypointName: string, version: number) {
  return useApi(
    () => contentService.checkContentUsage(waypointName, version),
    [waypointName, version],
    !!waypointName && !!version,
  );
}
